/**
 * Create Or Edit Connector Tool
 *
 * Create a new connector line or edit an existing connector on a slide.
 * Supports straight, elbow, and curved connectors with arrowheads.
 * Uses COM backend with BuildFreeform for unlimited multi-point paths.
 *
 */

import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import { ToolBase } from "../../../base/toolBase";
import { backendClient } from "../../../../../services/communication/backendClient";
import {
    CreateOrEditConnectorParamsSchema,
    type CreateOrEditConnectorParams,
} from "./createOrEditConnector.schema";
import { getOrCreatedocumentId } from "../../../../../services/documentIdManager";
import { applyShapeMetadata } from "../../../../../services/metadataService";

// ═══════════════════════════════════════════════════════════════════════════
// RESPONSE TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create or edit connector result
 */
export interface CreateOrEditConnectorResult {
    success: boolean;
    slideId: number;
    shapeId: number;
    operationType: "create" | "edit";
    connectorType: string;
    points: Array<{ x: number; y: number }>;
    warnings: string[];
    errors: string[];
}

// ═══════════════════════════════════════════════════════════════════════════
// CREATE OR EDIT CONNECTOR TOOL
// ═══════════════════════════════════════════════════════════════════════════

// System prompt fallback for Keywords AI
const CONNECTOR_SYSTEM_PROMPT = `### createOrEditConnector
Create or edit connectors (straight, elbow, curved) to show relationships between shapes or steps in a flow. Use connectors for timelines, chart annotations, flow/process diagrams, entity diagrams, and callout relationships. Prefer the simplest routing that communicates the relationship clearly, avoid crossing labels or key content, and keep point counts minimal to avoid jagged paths. Use \`elbow\` for grid/orthogonal layouts, \`curved\` for organic flows, and \`straight\` for clean, direct links.

**Use case overview:**
- Timelines: connect milestones to dates or bands; use straight/elbow lines that stay parallel to the timeline and avoid label collisions
- Chart annotations: short leader lines that point to a specific bar/point with minimal routing
- Flow/process diagrams: elbow connectors to keep right angles and preserve grid alignment
- Entity diagrams: curved or elbow connectors to show non-orthogonal relationships without crossing key labels
- Callout relationships: short connectors with arrowheads to tie a label to a target element
- Multi-step sequences: consistent connector styling to show directionality across steps

**How to use the features:**
- \`points\` define the connector path; keep points minimal and route around content
- \`lineStyle\` controls color/weight/dash for hierarchy or emphasis
- \`arrowheadStart\` / \`arrowheadEnd\` indicate directionality
- Use multiple points only when you need to avoid collisions or clarify flow

If you’re rerouting connectors to make room for new content, do that space planning first, then apply connector edits here.

\`\`\`json
{
  "slideId": 1,
  "operationType": "create",
  "connectorType": "elbow",
  "points": [
    { "x": 100, "y": 100 },
    { "x": 300, "y": 200 }
  ],
  "lineStyle": { "color": "#000000", "weight": 2 },
  "endArrowhead": { "type": "triangle" }
}
\`\`\``;

/**
 * Tool for creating or editing connectors on slides.
 *
 * Features:
 * - Create straight, elbow, or curved connectors
 * - Edit existing connectors by ID
 * - Line styling (color, weight, dash style)
 * - Arrowheads at start and/or end points
 *
 */
export class CreateOrEditConnectorTool extends ToolBase<CreateOrEditConnectorParams, CreateOrEditConnectorResult> {
    public readonly name = "createOrEditConnector";
    public readonly description: ToolDescription;

    // System prompt description
    public readonly systemPromptDescription = CONNECTOR_SYSTEM_PROMPT;

    // Streaming operation fields
    fieldsToCheck = [
        { name: 'slideId', type: 'number' as const, list_status: false, order: 1 },
        { name: 'operationType', type: 'string' as const, list_status: false, order: 2 },
        { name: 'shapeId', type: 'number' as const, list_status: false, order: 3 },
    ];

    listsToCheck = [];

    constructor() {
        super();
        this.description = new ToolDescription(
            "createOrEditConnector",
            `Create or edit connectors (straight, elbow, curved) to show relationships between shapes or steps in a flow. Use connectors for timelines, chart annotations, flow/process diagrams, entity diagrams, multi-step sequences, and callout relationships. Prefer the simplest routing that communicates the relationship clearly, avoid crossing labels or key content, and keep point counts minimal to avoid jagged paths. Use \`elbow\` for grid/orthogonal layouts, \`curved\` for organic flows, and \`straight\` for clean, direct links.

**Use case overview:**
- Timelines: connect milestones to dates or bands; keep lines parallel to the timeline and off label text
- Chart annotations: short leader lines that point to a specific bar/point with minimal routing
- Flow/process diagrams: elbow connectors to preserve grid alignment and spacing
- Entity diagrams: curved or elbow connectors to show non-orthogonal relationships without crossing key labels
- Callout relationships: short connectors with arrowheads to tie a label to a target element
- Multi-step sequences: consistent connector styling to show directionality across steps

If you’re rerouting connectors to make room for new content, do that space planning first, then apply connector edits here.

**Operation Types:**
- \`create\`: Create new connector
- \`edit\`: Modify existing connector (requires shapeId)

**Connector Types:**
- \`straight\`: Direct line between points
- \`elbow\`: Right-angle connector
- \`curved\`: Curved/bezier connector

**Features:**
- Line styling: color, weight, dash style
- Arrowheads at start and/or end
- Multiple points for complex paths

**Arrowhead Styles:**
none, triangle, oval, stealth, diamond, open

**Example (Create):**
\`\`\`json
{
  "slideId": 1,
  "operationType": "create",
  "connectorConfig": {
    "type": "elbow",
    "points": [
      { "x": 100, "y": 100 },
      { "x": 300, "y": 200 }
    ],
    "lineStyle": {
      "color": "#333333",
      "weight": 1.5,
      "dashStyle": "solid"
    },
    "arrowheadEnd": {
      "style": "triangle",
      "length": "medium",
      "width": "medium"
    }
  }
}
\`\`\`

**Example (Edit):**
\`\`\`json
{
  "slideId": 1,
  "operationType": "edit",
  "shapeId": 10,
  "connectorConfig": {
    "type": "curved",
    "points": [
      { "x": 150, "y": 100 },
      { "x": 350, "y": 250 }
    ],
    "lineStyle": {
      "color": "#0066CC",
      "weight": 2.0
    }
  }
}
\`\`\``,
            CreateOrEditConnectorParamsSchema
        );
    }

    public async execute(parameters: unknown): Promise<CreateOrEditConnectorResult> {
        const parseResult = safeParseToolParams(CreateOrEditConnectorParamsSchema, parameters);
        if (!parseResult.success) {
            throw new Error(`Invalid parameters: ${parseResult.error}`);
        }

        const params = parseResult.data;
        const isCreate = params.operationType === "create";
        const { connectorConfig } = params;

        console.log(`[CreateOrEditConnectorTool] ${isCreate ? 'Creating' : 'Editing'} connector on slide ${params.slideId}`);
        console.log(`  Type: ${connectorConfig.type}`);
        console.log(`  Points: ${connectorConfig.points.length}`);
        if (!isCreate) {
            console.log(`  Shape ID: ${params.shapeId}`);
        }

        // Get deck ID for COM backend
        const documentId = await getOrCreatedocumentId();

        // Call COM backend
        const comResult = await backendClient.comCreateOrEditConnector({
            documentId,
            slideId: params.slideId,
            operationType: params.operationType,
            shapeId: params.shapeId,
            connectorConfig: {
                type: connectorConfig.type,
                points: connectorConfig.points,
                lineStyle: connectorConfig.lineStyle,
                arrowheadStart: connectorConfig.arrowheadStart,
                arrowheadEnd: connectorConfig.arrowheadEnd,
            },
        });

        console.log(`[CreateOrEditConnectorTool] COM backend response:`, comResult);

        if (!comResult.success) {
            const errorMsg = comResult.errors?.join(', ') || comResult.message || 'Unknown error';
            throw new Error(`Connector operation failed: ${errorMsg}`);
        }

        const result: CreateOrEditConnectorResult = {
            success: comResult.success,
            slideId: params.slideId,
            shapeId: comResult.shapeId ?? 0,
            operationType: params.operationType,
            connectorType: connectorConfig.type,
            points: connectorConfig.points,
            warnings: comResult.warnings ?? [],
            errors: comResult.errors ?? [],
        };

        console.log(`[CreateOrEditConnectorTool] ${isCreate ? 'Create' : 'Edit'} complete:`,
            `connector ${result.shapeId}, ${result.warnings.length} warnings, ${result.errors.length} errors`);

        return await this.finalizeResult(params, result);
    }

    private async finalizeResult(
        params: CreateOrEditConnectorParams,
        result: CreateOrEditConnectorResult
    ): Promise<CreateOrEditConnectorResult> {
        if (!result.success) {
            return result;
        }

        const shapeId = String(result.shapeId ?? params.shapeId ?? "");
        if (shapeId) {
            await applyShapeMetadata({
                slideId: params.slideId,
                shapeId,
                metadata: params.metadata,
            });
        }

        return result;
    }
}
